import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";
import { CSS_NAMED_COLORS, HEX_TO_CSS_NAME } from "../data/cssNamedColors";

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

const hexToRgb = (hex) => {
  const m = hex.trim().replace(/^#/, "");
  let r;
  let g;
  let b;
  let a = 1;
  if (/^[0-9a-f]{3}$/i.test(m)) {
    r = parseInt(m[0] + m[0], 16);
    g = parseInt(m[1] + m[1], 16);
    b = parseInt(m[2] + m[2], 16);
  } else if (/^[0-9a-f]{4}$/i.test(m)) {
    r = parseInt(m[0] + m[0], 16);
    g = parseInt(m[1] + m[1], 16);
    b = parseInt(m[2] + m[2], 16);
    a = parseInt(m[3] + m[3], 16) / 255;
  } else if (/^[0-9a-f]{6}$/i.test(m)) {
    r = parseInt(m.slice(0, 2), 16);
    g = parseInt(m.slice(2, 4), 16);
    b = parseInt(m.slice(4, 6), 16);
  } else if (/^[0-9a-f]{8}$/i.test(m)) {
    r = parseInt(m.slice(0, 2), 16);
    g = parseInt(m.slice(2, 4), 16);
    b = parseInt(m.slice(4, 6), 16);
    a = parseInt(m.slice(6, 8), 16) / 255;
  } else {
    return null;
  }
  return { r, g, b, a };
};

const parseRgbString = (input) => {
  const m = input.trim().match(
    /^rgba?\(\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*(?:[,/]\s*([\d.]+%?))?\s*\)$/i
  );
  if (!m) return null;
  const r = Math.round(clamp(parseFloat(m[1]), 0, 255));
  const g = Math.round(clamp(parseFloat(m[2]), 0, 255));
  const b = Math.round(clamp(parseFloat(m[3]), 0, 255));
  let a = 1;
  if (m[4] !== undefined) {
    a = m[4].endsWith("%") ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
  }
  return { r, g, b, a: clamp(a, 0, 1) };
};

const parseHslString = (input) => {
  const m = input.trim().match(
    /^hsla?\(\s*([\d.]+)(deg|rad|turn)?\s*[, ]\s*([\d.]+)%\s*[, ]\s*([\d.]+)%\s*(?:[,/]\s*([\d.]+%?))?\s*\)$/i
  );
  if (!m) return null;
  let h = parseFloat(m[1]);
  const unit = (m[2] || "deg").toLowerCase();
  if (unit === "rad") h = (h * 180) / Math.PI;
  else if (unit === "turn") h *= 360;
  h = ((h % 360) + 360) % 360;
  const s = clamp(parseFloat(m[3]), 0, 100);
  const l = clamp(parseFloat(m[4]), 0, 100);
  let a = 1;
  if (m[5] !== undefined) {
    a = m[5].endsWith("%") ? parseFloat(m[5]) / 100 : parseFloat(m[5]);
  }
  return hslToRgb(h, s, l, clamp(a, 0, 1));
};

const hslToRgb = (h, s, l, a = 1) => {
  const S = s / 100;
  const L = l / 100;
  const k = (n) => (n + h / 30) % 12;
  const f = (n) => {
    const c = Math.min(L, 1 - L) * S;
    return L - c * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
  };
  return {
    r: Math.round(f(0) * 255),
    g: Math.round(f(8) * 255),
    b: Math.round(f(4) * 255),
    a,
  };
};

const rgbToHsl = ({ r, g, b }) => {
  const R = r / 255;
  const G = g / 255;
  const B = b / 255;
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case R:
        h = (G - B) / d + (G < B ? 6 : 0);
        break;
      case G:
        h = (B - R) / d + 2;
        break;
      default:
        h = (R - G) / d + 4;
    }
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
};

const toHex = (n) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");

const formatHex = ({ r, g, b, a }) => {
  const base = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  if (a >= 1) return base;
  return `${base}${toHex(a * 255)}`;
};

const formatRgb = ({ r, g, b, a }) =>
  a >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${Number(a.toFixed(3))})`;

const formatHsl = (rgb) => {
  const { h, s, l } = rgbToHsl(rgb);
  return rgb.a >= 1
    ? `hsl(${h}, ${s}%, ${l}%)`
    : `hsla(${h}, ${s}%, ${l}%, ${Number(rgb.a.toFixed(3))})`;
};

const parseNamed = (input) => {
  const key = input.trim().toLowerCase();
  if (!(key in CSS_NAMED_COLORS)) return null;
  return hexToRgb(CSS_NAMED_COLORS[key]);
};

const parseAny = (raw) => {
  const input = raw.trim();
  if (!input) return null;
  if (input.startsWith("#") || /^[0-9a-f]{3,8}$/i.test(input)) {
    return hexToRgb(input);
  }
  if (/^rgba?\(/i.test(input)) return parseRgbString(input);
  if (/^hsla?\(/i.test(input)) return parseHslString(input);
  if (/^[a-z]+$/i.test(input)) return parseNamed(input);
  return null;
};

const findNamedMatch = (color) => {
  if (!color || color.a < 1) return null;
  const hexKey = `${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}`;
  return HEX_TO_CSS_NAME[hexKey] || null;
};

const ColorConverter = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState("#6366f1");

  const color = useMemo(() => parseAny(input), [input]);

  const outputs = useMemo(() => {
    if (!color) return null;
    return {
      hex: formatHex(color),
      rgb: formatRgb(color),
      hsl: formatHsl(color),
      named: findNamedMatch(color),
    };
  }, [color]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const preview = color ? formatRgb(color) : "transparent";

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("color_converter_title")}
            </h1>
            <p className="text-gray-600">{t("color_converter_intro")}</p>

            <div className="flex flex-wrap items-end gap-4">
              <div className="flex flex-col gap-1 flex-1 min-w-[240px]">
                <label className="text-sm font-medium text-gray-700">
                  {t("color_converter_input_label")}
                </label>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="#6366f1, rgb(99,102,241), hsl(239, 84%, 67%), tomato"
                  className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("color_converter_picker_label")}
                </label>
                <input
                  type="color"
                  value={color ? formatHex({ ...color, a: 1 }) : "#000000"}
                  onChange={(e) => setInput(e.target.value)}
                  className="w-16 h-10 p-0 border border-gray-300 rounded-md cursor-pointer bg-white"
                />
              </div>
            </div>

            <div
              className="h-28 rounded-md border border-indigo-200 shadow-inner flex items-center justify-center"
              style={{
                backgroundColor: preview,
                backgroundImage:
                  "linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)",
                backgroundSize: "16px 16px",
                backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
                backgroundBlendMode: "normal",
              }}
            >
              <div
                className="w-full h-full rounded-md flex items-center justify-center"
                style={{ backgroundColor: preview }}
              >
                {!color && (
                  <span className="text-gray-500 text-sm">
                    {t("color_converter_invalid")}
                  </span>
                )}
              </div>
            </div>

            {outputs && (
              <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                <ul className="flex flex-col divide-y divide-indigo-100">
                  {[
                    { key: "hex", label: "HEX", value: outputs.hex },
                    { key: "rgb", label: "RGB", value: outputs.rgb },
                    { key: "hsl", label: "HSL", value: outputs.hsl },
                    ...(outputs.named
                      ? [
                          {
                            key: "named",
                            label: t("color_converter_named_label"),
                            value: outputs.named,
                          },
                        ]
                      : []),
                  ].map((row) => (
                    <li
                      key={row.key}
                      className="flex items-center justify-between gap-3 py-2"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="inline-block w-12 text-xs font-semibold text-indigo-700 uppercase">
                          {row.label}
                        </span>
                        <span className="font-mono text-sm break-all text-gray-800">
                          {row.value}
                        </span>
                      </div>
                      <StandardButton
                        onClick={() => copy(row.value, row.label)}
                        type="secondary"
                        variant="outlined"
                        size="small"
                      >
                        {t("copy_button")}
                      </StandardButton>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("color_converter_note_formats")}</p>
              <p>{t("color_converter_note_alpha")}</p>
              <p>{t("color_converter_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default ColorConverter;
